import { flagsFromMemberRateOption, resolveMemberRateOption } from "./checkoutMemberRateHelpers";

/**
 * @typedef {'member_rate'|'member_rate_op'|'discount'|'none'} PricingModifierKind
 */

/**
 * Resolved discount item for pricing math (same semantics as member rate overrides).
 * - `null` override → user explicitly cleared / superseded (do not fall back to booking API).
 * - `undefined` override → use booking payload.
 *
 * @param {Object|null|undefined} override - discountByBookingItemId[id]
 * @param {Object|undefined} booking
 * @returns {Object|undefined} discount item or undefined
 */
export function resolveDiscountOption(override, booking) {
	if (override === null) return undefined;
	if (override !== undefined) return override;
	if (booking?.isDiscountApplied === false) return undefined;
	const fromPriceAdjustment = booking?.price_adjustment;
	if (fromPriceAdjustment) return fromPriceAdjustment?.item ?? fromPriceAdjustment;
	const fromDiscountOption = booking?.discount_option;
	return fromDiscountOption?.item ?? fromDiscountOption;
}

/**
 * True when the resolved discount item is a 100% (full) discount.
 * Matches either the display label or a percentage value of 100.
 *
 * @param {Object|null|undefined} discount
 * @returns {boolean}
 */
export function isFullDiscount(discount) {
	if (!discount) return false;
	return (
		discount.value_display === "100%" ||
		(discount.type === "percentage" && Number(discount.value) === 100)
	);
}

/**
 * Shape expected by DiscountDropdown `selectedOption`.
 *
 * @param {Object|null|undefined} override - discountByBookingItemId[id]
 * @param {Object|undefined} booking
 * @returns {{ item: Object }|undefined}
 */
export function getDiscountDropdownSelectedOption(override, booking) {
	if (override !== undefined) {
		return override ? { item: override } : undefined;
	}
	if (booking?.isDiscountApplied === false) return undefined;
	const fromBooking = booking?.price_adjustment ?? booking?.discount_option;
	if (fromBooking == null || fromBooking === false) return undefined;
	const normalizedItem = fromBooking?.item ?? fromBooking;
	return {
		item: {
			...normalizedItem,
			displayName:
				normalizedItem?.displayName ??
				normalizedItem?.title ??
				normalizedItem?.name ??
				normalizedItem?.label,
		},
	};
}

/**
 * Deep-merge a patch into the booking whose `id` matches (top-level lists only).
 *
 * @param {Record<string, Array>|null|undefined} rawCustomerBookingItems
 * @param {number|string} bookingItemId
 * @param {Object} partial
 * @returns {Record<string, Array>}
 */
function sameBookingItemId(left, right) {
	if (left == null || right == null) return false;
	return String(left) === String(right);
}

export function patchBookingItemInCustomerBookings(rawCustomerBookingItems, bookingItemId, partial) {
	if (!rawCustomerBookingItems || typeof rawCustomerBookingItems !== "object" || bookingItemId == null) {
		return rawCustomerBookingItems || {};
	}
	const next = {};
	for (const [customerName, bookings] of Object.entries(rawCustomerBookingItems)) {
		const list = Array.isArray(bookings) ? bookings : [];
		next[customerName] = list.map((booking) =>
			sameBookingItemId(booking?.id, bookingItemId) ? { ...booking, ...partial } : booking
		);
	}
	return next;
}

/**
 * All booking_item ids in raw checkout structure (single or multi-pax).
 *
 * @param {Record<string, Array>|null|undefined} rawCustomerBookingItems
 * @returns {Array<number|string>}
 */
export function collectAllBookingItemIds(rawCustomerBookingItems) {
	if (!rawCustomerBookingItems || typeof rawCustomerBookingItems !== "object") return [];
	const ids = [];
	for (const bookings of Object.values(rawCustomerBookingItems)) {
		const list = Array.isArray(bookings) ? bookings : [];
		for (const booking of list) {
			if (booking?.id != null) ids.push(booking.id);
		}
	}
	return ids;
}

/**
 * Map member rate dropdown item to booking_item flags (re-export pattern for callers).
 *
 * @param {Object|null|undefined} memberRateItem - { id: 1|2, title, ... } or null when clearing
 * @returns {{ isMembershipApplied: number, isMemberRateApplied: number }}
 */
export function membershipFlagsFromMemberRateItem(memberRateItem) {
	if (!memberRateItem) {
		return { isMembershipApplied: 0, isMemberRateApplied: 0 };
	}
	return flagsFromMemberRateOption({ item: memberRateItem });
}

/**
 * Pure description of which modifier wins for a row (for debugging / UI badges).
 *
 * @param {Object|null|undefined} memberRateOverride
 * @param {Object|null|undefined} discountOverride
 * @param {Object|undefined} booking
 * @returns {PricingModifierKind}
 */
/**
 * Combined checkout resolution: a live discount always wins over member rate.
 *
 * @param {Object|null|undefined} memberRateOverride
 * @param {Object|null|undefined} discountOverride
 * @param {Object|undefined} booking
 * @returns {{ item: { id: number, title: string } }|undefined}
 */
export function resolveCheckoutMemberRateOption(memberRateOverride, discountOverride, booking) {
	if (resolveDiscountOption(discountOverride, booking)) return undefined;
	return resolveMemberRateOption(memberRateOverride, booking);
}

/**
 * Next override + booking-item patch when a discount is selected or cleared.
 * Applying a discount clears member rate. Removing a discount keeps member rate
 * cleared — it must not fall back to stale booking flags or a previous override.
 *
 * @param {Object|null|undefined} discountItem
 * @returns {{
 *   memberRateOverride: null,
 *   discountOverride: Object|null,
 *   clearBookingMembership: boolean,
 *   bookingPatch: Object
 * }}
 */
export function nextStateOnDiscountSelect(discountItem) {
	if (discountItem != null) {
		return {
			memberRateOverride: null,
			discountOverride: discountItem,
			clearBookingMembership: true,
			bookingPatch: {
				isMembershipApplied: 0,
				isMemberRateApplied: 0,
				isDiscountApplied: true,
			},
		};
	}
	return {
		memberRateOverride: null,
		discountOverride: null,
		clearBookingMembership: false,
		bookingPatch: {
			isMembershipApplied: 0,
			isMemberRateApplied: 0,
			isDiscountApplied: false,
			discount_option: null,
			price_adjustment: null,
		},
	};
}

/**
 * Seed discount / member-rate overrides from API booking items.
 * A price_adjustment means do not seed member rate for that row.
 *
 * @param {Record<string, Array>|null|undefined} apiBookingItems
 * @returns {{ seededDiscounts: Record<string, Object>, seededMemberRates: Record<string, Object> }}
 */
export function buildSeededPricingOverrides(apiBookingItems) {
	const seededDiscounts = {};
	const seededMemberRates = {};
	if (!apiBookingItems || typeof apiBookingItems !== "object") {
		return { seededDiscounts, seededMemberRates };
	}

	for (const items of Object.values(apiBookingItems)) {
		for (const item of Array.isArray(items) ? items : []) {
			const id = item?.id ?? item?.booking_item_id;
			if (id == null) continue;

			const pa = item?.price_adjustment;
			const hasDiscount =
				pa != null && typeof pa === "object" && Object.keys(pa).length > 0;
			if (hasDiscount) {
				seededDiscounts[id] = pa?.item ?? pa;
				continue;
			}

			if (item?.isMemberRateApplied === 1 || item?.isMemberRateApplied === true) {
				seededMemberRates[id] = { id: 2, title: "Member Rate (OP)" };
			} else if (
				(item?.isMemberRateApplied === 0 || item?.isMemberRateApplied === false) &&
				(item?.isMembershipApplied === 1 || item?.isMembershipApplied === true)
			) {
				seededMemberRates[id] = { id: 1, title: "Member Rate" };
			}
		}
	}

	return { seededDiscounts, seededMemberRates };
}

/**
 * Merge API-seeded overrides without replacing a live local override.
 * `null` is a live "user cleared" value and must not be overwritten.
 *
 * @param {Record<string, Object|null|undefined>} prev
 * @param {Record<string, Object>} seeded
 * @returns {Record<string, Object|null|undefined>}
 */
export function mergePricingOverrideMap(prev = {}, seeded = {}) {
	const merged = { ...prev };
	for (const [id, val] of Object.entries(seeded)) {
		if (merged[id] === undefined) {
			merged[id] = val;
		}
	}
	return merged;
}

export function getActivePricingModifierKind(memberRateOverride, discountOverride, booking) {
	const discountItem = resolveDiscountOption(discountOverride, booking);
	const hasDiscount = discountItem != null;

	if (hasDiscount) return "discount";

	if (memberRateOverride === null) return "none";
	if (memberRateOverride && (memberRateOverride.id === 1 || memberRateOverride.id === 2)) {
		return memberRateOverride.id === 2 ? "member_rate_op" : "member_rate";
	}

	if (booking?.isMemberRateApplied === 1 || booking?.isMemberRateApplied === true) return "member_rate_op";
	if (
		(booking?.isMemberRateApplied === 0 || booking?.isMemberRateApplied === false) &&
		(booking?.isMembershipApplied === 1 || booking?.isMembershipApplied === true)
	) {
		return "member_rate";
	}
	return "none";
}

/**
 * Flatten bookingDetails.booking_item into a list of booking-item-like objects.
 *
 * @param {unknown} rawBookingItems
 * @returns {Array<Object>}
 */
export function flattenBookingItems(rawBookingItems) {
	if (Array.isArray(rawBookingItems)) return rawBookingItems;
	if (!rawBookingItems || typeof rawBookingItems !== "object") return [];

	// bookingDetails.booking_item can be either:
	// - array of booking items
	// - object keyed by customer/pax whose values are booking arrays
	// - single booking item object
	if ("price_adjustment" in rawBookingItems || "id" in rawBookingItems) {
		return [rawBookingItems];
	}

	return Object.values(rawBookingItems).flatMap((value) => {
		if (Array.isArray(value)) return value;
		if (!value || typeof value !== "object") return [];
		return "price_adjustment" in value || "id" in value ? [value] : [];
	});
}

/**
 * True when any booking item has a non-empty price_adjustment object.
 *
 * @param {unknown} rawBookingItems
 * @returns {boolean}
 */
export function hasBookingPriceAdjustmentObject(rawBookingItems) {
	return flattenBookingItems(rawBookingItems).some(
		(item) =>
			item?.price_adjustment != null &&
			typeof item.price_adjustment === "object" &&
			Object.keys(item.price_adjustment).length > 0
	);
}
